import { Button } from "@/components/core/buttons/button";
import Combobox from "@/components/core/forms/combobox";
import Select from "@/components/core/forms/select";
import { Content } from "@/components/layouts/content";
import { leadCategoriesQueryOptions, leadStatusesQueryOptions, useLeadImportBatchMutation } from "@/features/dashboard/api/queries/lead-query";
import { IconArrowLeft, IconTrash, IconUpload } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { read, utils } from "xlsx";

interface ImportRow {
	_key: number;
	reference_number: string;
	proposal_date: string;
	project_description: string;
	client_name: string;
	amount: number;
	contact_person: string;
	contact_number: string;
	email: string;
	client_address: string;
	prepared_by: string;
	prepared_by_label: string;
	status: string;
	remarks: string;
	category: string;
	_hasError: boolean;
}

// statusOptions and statusMap are built dynamically from the API in the component

async function loadUsers(inputValue: string) {
	const response = await fetch(`/api/user-approvals/all?search=${encodeURIComponent(inputValue)}`);
	const data = await response.json();
	const users = data.data || data;
	return users.map((user: { id: string; first_name: string; last_name: string; email: string }) => ({
		value: user.id,
		label: `${user.first_name} ${user.last_name}`.trim() || user.email,
	}));
}

async function fetchAllUsers(): Promise<{ id: string; name: string }[]> {
	const response = await fetch("/api/user-approvals/all");
	const data = await response.json();
	const users = data.data || data;
	return users.map((user: { id: string; first_name: string; last_name: string; email: string }) => ({
		id: user.id,
		name: `${user.first_name} ${user.last_name}`.trim() || user.email,
	}));
}

function matchUser(name: string, users: { id: string; name: string }[]): { id: string; name: string } | null {
	if (!name) return null;
	const lower = name.toLowerCase().trim();
	// Exact match
	const exact = users.find((u) => u.name.toLowerCase() === lower);
	if (exact) return exact;
	// Partial match
	const partial = users.find((u) => u.name.toLowerCase().includes(lower) || lower.includes(u.name.toLowerCase()));
	return partial || null;
}

function parseExcelDate(value: unknown): string {
	if (!value) return "";
	if (typeof value === "number") {
		// Excel serial date: days since 1899-12-30
		const date = new Date((value - 25569) * 86400 * 1000);
		return date.toISOString().split("T")[0];
	}
	const str = String(value);
	const parsed = new Date(str);
	if (!Number.isNaN(parsed.getTime())) {
		return parsed.toISOString().split("T")[0];
	}
	return str;
}

function parseAmount(value: unknown): number {
	if (typeof value === "number") return value;
	if (!value) return 0;
	return Number.parseFloat(String(value).replace(/[^\d.]/g, "")) || 0;
}

export const Route = createFileRoute("/_dashboard/leads_/import")({
	component: LeadsImportPage,
});

function LeadsImportPage() {
	const navigate = useNavigate();
	const importMutation = useLeadImportBatchMutation();
	const statusesQuery = useQuery(leadStatusesQueryOptions());
	const allStatuses = statusesQuery.data || [];
	const statusOptions = allStatuses.map((s) => ({ value: s.slug, label: s.name }));
	const statusMap: Record<string, string> = {};
	for (const s of allStatuses) {
		statusMap[s.name.toLowerCase()] = s.slug;
		statusMap[s.slug] = s.slug;
	}
	const defaultSlug = allStatuses.find((s) => s.is_default)?.slug || allStatuses[0]?.slug || "proposal_sent";
	const categoriesQuery = useQuery(leadCategoriesQueryOptions());
	const existingCategories = categoriesQuery.data || [];
	const [rows, setRows] = useState<ImportRow[]>([]);
	const [category, setCategory] = useState("");
	const [fileName, setFileName] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);
	const keyCounter = useRef(0);

	const handleFileSelect = useCallback(
		async (file: File) => {
			setFileName(file.name);
			const buffer = await file.arrayBuffer();
			const wb = read(buffer, { type: "array" });
			const sheet = wb.Sheets[wb.SheetNames[0]];
			const jsonRows = utils.sheet_to_json<Record<string, unknown>>(sheet, { header: "A" });

			if (jsonRows.length < 2) {
				toast.error("No data rows found in the file.");
				return;
			}

			// Remove header row
			jsonRows.shift();

			// Fetch users for auto-matching
			const users = await fetchAllUsers();

			const parsed: ImportRow[] = jsonRows
				.filter((r) => {
					const ref = String(r.A || "").trim();
					return ref.length > 0;
				})
				.map((r) => {
					const preparedByName = String(r.J || "").trim();
					const matched = matchUser(preparedByName, users);
					const rawStatus = String(r.K || "proposal sent")
						.toLowerCase()
						.trim();

					return {
						_key: keyCounter.current++,
						reference_number: String(r.A || "").trim(),
						proposal_date: parseExcelDate(r.B),
						project_description: String(r.C || "").trim(),
						client_name: String(r.D || "").trim(),
						amount: parseAmount(r.E),
						contact_person: String(r.F || "").trim(),
						contact_number: String(r.G || "").trim(),
						email: String(r.H || "").trim(),
						client_address: String(r.I || "").trim(),
						prepared_by: matched?.id || "",
						prepared_by_label: matched?.name || preparedByName,
						status: statusMap[rawStatus] || defaultSlug,
						remarks: String(r.L || "").trim(),
						category: String(r.O || "").trim(),
						_hasError: !matched,
					};
				});

			setRows(parsed);
		},
		[statusMap, defaultSlug],
	);

	const updateRow = useCallback((key: number, changes: Partial<ImportRow>) => {
		setRows((prev) =>
			prev.map((r) => {
				if (r._key !== key) return r;
				const updated = { ...r, ...changes };
				updated._hasError = !updated.prepared_by;
				return updated;
			}),
		);
	}, []);

	const deleteRow = useCallback((key: number) => {
		setRows((prev) => prev.filter((r) => r._key !== key));
	}, []);

	const handleImport = async () => {
		if (!rows.length) return;

		const rowsWithErrors = rows.filter((r) => r._hasError);
		if (rowsWithErrors.length > 0) {
			toast.error(`${rowsWithErrors.length} row(s) have unmatched "Prepared By" users. Please fix or remove them.`);
			return;
		}

		// Apply category override if set
		const leads = rows.map((r) => ({
			reference_number: r.reference_number,
			proposal_date: r.proposal_date,
			project_description: r.project_description,
			client_name: r.client_name,
			amount: r.amount,
			contact_person: r.contact_person || null,
			contact_number: r.contact_number || null,
			email: r.email || null,
			client_address: r.client_address || null,
			prepared_by: r.prepared_by,
			status: r.status,
			remarks: r.remarks || null,
			category: category || r.category,
		}));

		importMutation.mutate(leads, {
			onSuccess: (data) => {
				toast.success(data.message || `${data.imported} lead(s) imported.`);
				if (data.errors?.length) {
					for (const err of data.errors) {
						toast.warning(err);
					}
				}
				navigate({ to: "/leads" });
			},
			onError: (error: unknown) => {
				const axiosErr = error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
				const msg = axiosErr.response?.data?.message || "Failed to import leads.";
				toast.error(msg);
			},
		});
	};

	return (
		<Content pageName="Import Leads">
			<div className="flex flex-col w-full max-w-[1600px] p-6 gap-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<Button variant="ghost" size="sm" onClick={() => navigate({ to: "/leads" })}>
							<IconArrowLeft size={18} />
						</Button>
						<div>
							<h1 className="text-xl font-semibold text-foreground-dark">Import Leads</h1>
							<p className="text-sm text-foreground/70">
								{fileName ? `File: ${fileName} — ${rows.length} row(s)` : "Select an Excel file to preview and import leads"}
							</p>
						</div>
					</div>
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => navigate({ to: "/leads" })}>
							Cancel
						</Button>
						<Button onClick={handleImport} disabled={!rows.length || importMutation.isPending}>
							<IconUpload size={18} />
							{importMutation.isPending ? "Importing..." : `Import ${rows.length} Lead(s)`}
						</Button>
					</div>
				</div>

				{/* File picker & category */}
				<div className="flex items-end gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
					<label className="flex flex-col gap-1.5">
						<span className="text-sm font-medium text-gray-700">Excel File (.xlsx)</span>
						<input
							ref={fileInputRef}
							type="file"
							accept=".xlsx,.xls"
							onChange={(e) => {
								const f = e.target.files?.[0];
								if (f) handleFileSelect(f);
							}}
							className="px-3 py-2 border border-gray-200 rounded-lg text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
						/>
					</label>
					<label className="flex flex-col gap-1.5 w-64">
						<span className="text-sm font-medium text-gray-700">Category (override all rows)</span>
						<input
							type="text"
							list="import-category-options"
							value={category}
							onChange={(e) => setCategory(e.target.value)}
							placeholder="Type or select a category..."
							className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
						/>
						<datalist id="import-category-options">
							{existingCategories.map((cat) => (
								<option key={cat} value={cat} />
							))}
						</datalist>
					</label>
				</div>

				{/* Table */}
				{rows.length > 0 && (
					<div className="border border-gray-200 rounded-lg overflow-auto max-h-[calc(100vh-280px)]">
						<table className="w-full text-sm border-collapse">
							<thead className="sticky top-0 z-10 bg-gray-50">
								<tr>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b w-8">#</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[120px]">Ref No.</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[110px]">Proposal Date</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[200px]">Project Description</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[150px]">Client Name</th>
									<th className="px-2 py-2 text-right font-medium text-gray-600 border-b min-w-[100px]">Amount</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[130px]">Contact Person</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[110px]">Contact No.</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[150px]">Email</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[180px]">Prepared By</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[140px]">Status</th>
									<th className="px-2 py-2 text-left font-medium text-gray-600 border-b min-w-[150px]">Remarks</th>
									<th className="px-2 py-2 text-center font-medium text-gray-600 border-b w-10" />
								</tr>
							</thead>
							<tbody>
								{rows.map((row, idx) => (
									<ImportTableRow key={row._key} row={row} index={idx} onUpdate={updateRow} onDelete={deleteRow} />
								))}
							</tbody>
						</table>
					</div>
				)}

				{rows.length === 0 && (
					<div className="flex items-center justify-center h-64 text-gray-400">
						<p>Select an Excel file above to preview rows.</p>
					</div>
				)}
			</div>
		</Content>
	);
}

const ImportTableRow = React.memo(
	({
		row,
		index,
		onUpdate,
		onDelete,
	}: {
		row: ImportRow;
		index: number;
		onUpdate: (key: number, changes: Partial<ImportRow>) => void;
		onDelete: (key: number) => void;
	}) => {
		return (
			<tr className={clsx("border-b border-gray-100 hover:bg-gray-50/50", row._hasError && "bg-red-50/50")}>
				<td className="px-2 py-1 text-gray-400 text-xs">{index + 1}</td>
				<td className="px-1 py-1">
					<input
						type="text"
						value={row.reference_number}
						onChange={(e) => onUpdate(row._key, { reference_number: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="date"
						value={row.proposal_date}
						onChange={(e) => onUpdate(row._key, { proposal_date: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="text"
						value={row.project_description}
						onChange={(e) => onUpdate(row._key, { project_description: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="text"
						value={row.client_name}
						onChange={(e) => onUpdate(row._key, { client_name: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="number"
						step="0.01"
						value={row.amount}
						onChange={(e) => onUpdate(row._key, { amount: Number.parseFloat(e.target.value) || 0 })}
						className="w-full px-2 py-1 text-sm text-right border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="text"
						value={row.contact_person}
						onChange={(e) => onUpdate(row._key, { contact_person: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="text"
						value={row.contact_number}
						onChange={(e) => onUpdate(row._key, { contact_number: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="text"
						value={row.email}
						onChange={(e) => onUpdate(row._key, { email: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1">
					<div className={clsx(row._hasError && "ring-2 ring-red-300 rounded")}>
						<Combobox
							placeholder="Select user"
							loadOptions={loadUsers}
							defaultOptions
							clearable
							returnFullOption
							onValueChange={(option) => {
								const opt = option as { value?: string | number | null; label?: string | number | null } | null;
								onUpdate(row._key, {
									prepared_by: String(opt?.value || ""),
									prepared_by_label: String(opt?.label || ""),
								});
							}}
							defaultValue={row.prepared_by || undefined}
							placeholderValue={
								row.prepared_by
									? { value: row.prepared_by, label: row.prepared_by_label }
									: row.prepared_by_label
										? { value: null, label: row.prepared_by_label }
										: undefined
							}
						/>
					</div>
				</td>
				<td className="px-1 py-1">
					<Select
						placeholder="Status"
						options={statusOptions}
						defaultValue={row.status}
						onValueChange={(v) => onUpdate(row._key, { status: String(v || "proposal_sent") })}
					/>
				</td>
				<td className="px-1 py-1">
					<input
						type="text"
						value={row.remarks}
						onChange={(e) => onUpdate(row._key, { remarks: e.target.value })}
						className="w-full px-2 py-1 text-sm border border-transparent rounded hover:border-gray-300 focus:border-blue-400 focus:outline-none"
					/>
				</td>
				<td className="px-1 py-1 text-center">
					<button type="button" onClick={() => onDelete(row._key)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Remove row">
						<IconTrash size={16} />
					</button>
				</td>
			</tr>
		);
	},
	(prev, next) => prev.row === next.row && prev.index === next.index,
);
